import React, { useState, useEffect } from 'react';
import { X, Check, Mail, Clock } from 'lucide-react';
import TimeSelect from './TimeSelect';

/**
 * EmailSettings Component
 * Slide-over panel for email configuration and automation settings.
 */
const EmailSettings = ({
  workflowsEnabled = true,
  setWorkflowsEnabled,
  onClose,
  isOpen = true,
}) => {
  // Settings state (persisted to localStorage)
  const [settings, setSettings] = useState({
    fromName: 'Errigal Country House Hotel',
    replyTo: 'events@errigalhotel.com',
    defaultCC: '',
    sendWindowStart: '08:00',
    sendWindowEnd: '18:00',
  });

  const [hasChanges, setHasChanges] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('errigal_email_settings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings((prev) => ({ ...prev, ...parsed }));
      } catch (e) {
        console.error('Failed to load email settings:', e);
      }
    }
  }, []);

  // Handle setting changes
  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  // Save settings to localStorage
  const saveSettings = () => {
    localStorage.setItem('errigal_email_settings', JSON.stringify(settings));
    setHasChanges(false);
  };

  const handleClose = () => {
    if (hasChanges) {
      // Optionally warn before closing
      if (window.confirm('You have unsaved changes. Close anyway?')) {
        onClose?.();
      }
    } else {
      onClose?.();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={handleClose}
      />

      {/* Slide-over panel */}
      <div className="fixed right-0 top-0 h-screen w-full sm:w-96 bg-white shadow-xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Email Settings</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
          {/* General Settings Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Mail size={18} className="text-emerald-600" />
              <h3 className="font-semibold text-gray-900">General Settings</h3>
            </div>

            <div className="space-y-4">
              {/* From Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default From Name
                </label>
                <input
                  type="text"
                  value={settings.fromName}
                  onChange={(e) =>
                    handleSettingChange('fromName', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  placeholder="Your organization name"
                />
              </div>

              {/* Reply-to Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Reply-to Email
                </label>
                <input
                  type="email"
                  value={settings.replyTo}
                  onChange={(e) =>
                    handleSettingChange('replyTo', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  placeholder="reply@example.com"
                />
              </div>

              {/* Default CC */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default CC (Optional)
                </label>
                <input
                  type="email"
                  value={settings.defaultCC}
                  onChange={(e) =>
                    handleSettingChange('defaultCC', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  placeholder="cc@example.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave blank to disable automatic CC
                </p>
              </div>
            </div>
          </div>

          {/* Automation Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Clock size={18} className="text-emerald-600" />
              <h3 className="font-semibold text-gray-900">Automation</h3>
            </div>

            <div className="space-y-4">
              {/* Workflows toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Email Suggestions
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {workflowsEnabled
                      ? 'Smart suggestions enabled'
                      : 'Smart suggestions disabled'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setWorkflowsEnabled?.(!workflowsEnabled);
                    setHasChanges(true);
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    workflowsEnabled
                      ? 'bg-emerald-600'
                      : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      workflowsEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Send window */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Sending Window
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-gray-600 block mb-1">
                      Start Time
                    </label>
                    <TimeSelect
                      value={settings.sendWindowStart}
                      onChange={(e) =>
                        handleSettingChange('sendWindowStart', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-gray-600 block mb-1">
                      End Time
                    </label>
                    <TimeSelect
                      value={settings.sendWindowEnd}
                      onChange={(e) =>
                        handleSettingChange('sendWindowEnd', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Scheduled emails will only send during these hours
                </p>
              </div>
            </div>
          </div>

          {/* Connection Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Mail size={18} className="text-emerald-600" />
              <h3 className="font-semibold text-gray-900">Email Connection</h3>
            </div>

            <div className="space-y-3">
              {/* SMTP Status */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-block w-2 h-2 bg-blue-500 rounded-full" />
                  <p className="text-sm font-medium text-blue-900">
                    Demo Mode
                  </p>
                </div>
                <p className="text-xs text-blue-700">
                  Currently using demo mode. Emails are logged but not sent.
                </p>
              </div>

              {/* Test Send Button */}
              <button className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium flex items-center justify-center gap-2">
                <Mail size={16} />
                Send Test Email
              </button>

              {/* Info */}
              <p className="text-xs text-gray-600 text-center mt-4">
                Test emails are sent to: <br />
                <span className="font-medium text-gray-700">
                  events@errigalhotel.com
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Footer - Action buttons */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
          >
            Close
          </button>
          <button
            onClick={() => {
              saveSettings();
            }}
            disabled={!hasChanges}
            className={`flex-1 px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2 ${
              hasChanges
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Check size={16} />
            Save Changes
          </button>
        </div>
      </div>
    </>
  );
};

export default EmailSettings;
